import { useAuth } from "../lib/auth";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LogOut, Phone, Home, LayoutDashboard, Users, Calendar, Briefcase } from "lucide-react";
import { BRAND } from "../lib/constants";
import { Button } from "../components/ui/button";

const NAV = {
  admin: [
    { to: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { to: "/admin/cases", label: "Cases", icon: Briefcase },
    { to: "/admin/caregivers", label: "Caregivers", icon: Users },
  ],
  client: [
    { to: "/client", label: "My Care", icon: Briefcase },
    { to: "/client/new", label: "Request Care", icon: Calendar },
  ],
  caregiver: [
    { to: "/caregiver", label: "My Shifts", icon: Calendar },
    { to: "/caregiver/profile", label: "Availability", icon: LayoutDashboard },
  ],
};

export default function PortalLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const nav = NAV[user?.role] || [];

  const handleLogout = () => {
    logout();
    navigate("/auth");
  };

  return (
    <div className="min-h-screen bg-[#FFFFFF]">
      {/* Top bar */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FFFFFF]/90 border-b border-[#E5DDC9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="portal-logo">
            <div className="bg-[#FFFFFF] rounded-xl p-1 ring-1 ring-[#AC9667]/30">
              <img
                src="https://customer-assets.emergentagent.com/job_serene-home-care-1/artifacts/tqn1isrq_IMG_20251217_193656.jpg"
                alt="MPP"
                className="h-9 w-9 object-contain block"
              />
            </div>
            <div className="hidden sm:flex flex-col leading-tight">
              <span className="font-serif text-sm text-[#005165]">Peaceful Path</span>
              <span className="text-[9px] uppercase tracking-[0.22em] text-[#64748B]">
                {user?.role} portal
              </span>
            </div>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            {nav.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  data-testid={`portalnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={`flex items-center gap-2 px-4 h-10 rounded-full text-sm transition-colors ${
                    active
                      ? "bg-[#005165] text-[#FFFFFF]"
                      : "text-[#1F2937] hover:bg-[#F8F9FA]"
                  }`}
                >
                  <n.icon className="w-4 h-4" /> {n.label}
                </Link>
              );
            })}
          </div>

          <div className="flex items-center gap-2">
            <a
              href={`tel:${BRAND.phoneRaw}`}
              className="hidden sm:flex items-center gap-1 text-xs text-[#64748B] hover:text-[#AC9667]"
            >
              <Phone className="w-3.5 h-3.5" /> {BRAND.phone}
            </a>
            <Button
              variant="outline"
              onClick={handleLogout}
              data-testid="portal-logout"
              className="rounded-full h-10 border-[#005165]/15 text-[#005165] hover:bg-[#005165] hover:text-[#FFFFFF] px-3 sm:px-5"
            >
              <LogOut className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden border-t border-[#E5DDC9] bg-[#FFFFFF]">
          <div className="max-w-7xl mx-auto px-2 py-2 flex items-center gap-1 overflow-x-auto">
            {nav.map((n) => {
              const active = location.pathname === n.to;
              return (
                <Link
                  key={n.to}
                  to={n.to}
                  className={`flex items-center gap-1.5 px-3 h-9 rounded-full text-xs whitespace-nowrap ${
                    active
                      ? "bg-[#005165] text-[#FFFFFF]"
                      : "text-[#1F2937] bg-[#F8F9FA]"
                  }`}
                >
                  <n.icon className="w-3.5 h-3.5" /> {n.label}
                </Link>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {title && (
          <div className="mb-6 sm:mb-8">
            <h1 className="font-serif text-3xl sm:text-4xl text-[#005165] tracking-tight">{title}</h1>
            <p className="text-sm text-[#64748B] mt-1">
              Welcome back, <span className="text-[#005165]">{user?.name}</span>
            </p>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
